"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import HeadingCp from "../components/HeadingCp"
import SearchBarCP from "../components/SearchBarCP"
import DateFormat from "../components/DateFormat"
import LoadingSpinner from "../components/LoadingSpinner"
import SubMenu from "../components/SubMenu"

const ElectricalEstimateList = () => {
  const apiUrl = process.env.REACT_APP_API_URL
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [estimates, setEstimates] = useState([])
  const [filteredEstimates, setFilteredEstimates] = useState([])

  useEffect(() => {
    fetchEstimates()
  }, [])

  const fetchEstimates = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${apiUrl}/electrical_estimate_crud.php`, {
        params: { listEstimates: 1 },
      })

      const data = Array.isArray(response.data) ? response.data : []
      setEstimates(data)
      setFilteredEstimates(data)
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login")
      } else if (error.response?.status === 403) {
        toast.error(
          "Oops!! you don't have access to this area. Speak to the CFO.",
          { position: "top-center" }
        )
      } else {
        console.error("Error fetching electrical estimates:", error)
        toast.error("Error loading electrical estimates.", {
          position: "top-center",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  // Search: project_address, contact_name, employee_name, id
  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchTerm(value)

    if (value === "") {
      setFilteredEstimates(estimates)
    } else {
      const lower = value.toLowerCase()
      const filtered = estimates.filter((item) => {
        return (
          (item.project_address &&
            item.project_address.toLowerCase().includes(lower)) ||
          (item.contact_name &&
            item.contact_name.toLowerCase().includes(lower)) ||
          (item.employee_name &&
            item.employee_name.toLowerCase().includes(lower)) ||
          String(item.id).includes(lower)
        )
      })
      setFilteredEstimates(filtered)
    }
  }

  const handleSearchClick = () => {
    handleSearchChange({ target: { value: searchTerm } })
  }

  const getLinksForEstimate = (item) => [
    {
      name: "Open Estimator",
      click: () => navigate(`/electrical_estimate/${item.id}`),
    },
  ]

  return (
    <div className="mx-auto max-w-7xl">
      {/* Heading */}
      <div className="flex items-center justify-between">
        <div className="mt-2 mb-4 py-6">
          <HeadingCp label="Electrical Estimate List" />
        </div>
      </div>

      <br/>
      {/* New Estimate + Search */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* NEW ESTIMATE BUTTON */}
        <button
          type="button"
          onClick={() => navigate(`/electrical_estimate`)}
          className="inline-flex items-center rounded-md bg-blue-500 px-4 py-3 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          ➕ New Estimate
        </button>

        {/* Search Bar */}
        <SearchBarCP
          search={searchTerm}
          handleChange={handleSearchChange}
          onClick={handleSearchClick}
        />
      </div>

      {/* Table */}
      <div className="tableContainer">
        {loading ? (
          <LoadingSpinner>Loading estimates…</LoadingSpinner>
        ) : filteredEstimates.length === 0 ? (
          <p className="text-sm text-slate-500">No electrical estimates found.</p>
        ) : (
          <table className="min-w-full divide-y divide-x divide-slate-300 rounded-lg border bg-slate-200">
            <thead>
              <tr className="divide-x divide-slate-400">
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  ID
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Date
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Project
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Contact
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Estimator
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredEstimates.map((item, index) => (
                <tr
                  key={item.id}
                  className={`${index % 2 === 0 ? "bg-white" : "bg-slate-50"} divide-x`}
                >
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-slate-900">
                    <SubMenu
                      label={item.id}
                      idItem={item.id}
                      links={getLinksForEstimate(item)}
                    />
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    <DateFormat date={item.date} />
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-800">
                    {item.project_address || "-"}
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    {item.contact_name || "-"}
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    {item.employee_name || "-"}
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    {item.status || "pending"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default ElectricalEstimateList
